import { EMPTY_MESSAGE } from '@front-app/components/form/tiptap/useTiptapEditor';
import { isEmptyString } from '@front-app/util/string';
import {
	AnyHighlightComponentRequest,
	ElementType,
	HighlightsRichTextComponentRequest,
	HighlightsImageComponentRequest,
	HighlightsSurveyComponentRequest,
	HighlightsVideoComponentRequest,
	HighlightsHeadingComponentRequest,
	HighlightsButtonComponentRequest,
	HighlightsButtonAction,
	UnfilledHighlightsButtonComponent,
} from '../model';
import { isUnfilledButton } from './model';

export const validateItem = (item: AnyHighlightComponentRequest): string[] => {
	const { type } = item;
	switch (type) {
	case ElementType.TEXT:
		return validateTextRequest(item);
	case ElementType.HEADING:
		return validateHeadingRequest(item);
	case ElementType.IMAGE:
		return validateImageRequest(item);
	case ElementType.BUTTON:
		return validateButtonRequest(item);
	case ElementType.SURVEY:
		return validateSurveyRequest(item);
	case ElementType.VIDEO:
		return validateVideoRequest(item);
	default:
		// eslint-disable-next-line
		const unwanted: never = type;
		throw new Error(`Unable to satisfy component validation request for type: ${unwanted}`);
	}
};

function validateTextRequest(item: HighlightsRichTextComponentRequest) {
	if (isEmptyString(item.meta.mainText) || item.meta.mainText === EMPTY_MESSAGE) {
		return ['highlights.validation.text-field-required'];
	}
	return [];
}

function validateHeadingRequest(item: HighlightsHeadingComponentRequest) {
	if (isEmptyString(item.meta.mainText) || item.meta.mainText === EMPTY_MESSAGE) {
		return ['highlights.validation.heading-field-required'];
	}
	return [];
}

function validateImageRequest(item: HighlightsImageComponentRequest) {
	const errors: string[] = [];
	if (isEmptyString(item.meta.src)) {
		errors.push('highlights.validation.image-required');
	}
	if (isEmptyString(item.meta.altText)) {
		errors.push('highlights.validation.image-description-required');
	}
	return errors;
}

function validateVideoRequest(item: HighlightsVideoComponentRequest) {
	const errors: string[] = [];
	if (isEmptyString(item.meta.videoId)) {
		errors.push('highlights.validation.video-required');
	}
	return errors;
}

function validateSurveyRequest(item: HighlightsSurveyComponentRequest) {
	const errors: string[] = [];
	if (isEmptyString(item.meta.type)) {
		errors.push('highlights.validation.survey-type-required');
	}
	if (isEmptyString(item.meta.surveyId)) {
		errors.push('highlights.validation.survey-id-required');
	}
	return errors;
}

function validateButtonRequest(item: HighlightsButtonComponentRequest | UnfilledHighlightsButtonComponent) {
	if (isUnfilledButton(item)) {
		return [];
	}
	const { action } = item.meta;
	switch (action) {
	case HighlightsButtonAction.LINK_EXTERNAL:
		return applyValidators(item, [requireMainText, requireHref]);
	case HighlightsButtonAction.VIEW_EXTERNAL_VIDEO:
		return applyValidators(item, [requireMainText, requireHref]);
	case HighlightsButtonAction.DOWNLOAD_FILE_INTERNAL:
		return applyValidators(item, [requireMainText, requireContent]);
	case HighlightsButtonAction.DOWNLOAD_FILE_EXTERNAL:
		return applyValidators(item, [requireMainText, requireHref]);
	case HighlightsButtonAction.MESSAGE_USER:
		return applyValidators(item, [requireMainText, requireUserId]);
	case HighlightsButtonAction.REFER_COLLEAGUE_TO_GROUP:
		return applyValidators(item, [requireMainText, requireHref]);
	case HighlightsButtonAction.SUBMIT_SUGGESTION:
		return applyValidators(item, [requireMainText, requireHref]);
	case HighlightsButtonAction.START_QUIZ:
		return applyValidators(item, [requireMainText, requireHref]);
	case HighlightsButtonAction.CONTENT_SEARCH:
		return applyValidators(item, [requireMainText, requireCategories]);
	case HighlightsButtonAction.VIEW_EVENT:
		return applyValidators(item, [requireMainText, requireEvent]);
	default:
		// eslint-disable-next-line
		const unwanted: never = action;
		throw new Error(`Unable to satisfy button validation request for action: ${unwanted}`);
	}
}

type ButtonValidator = (item: HighlightsButtonComponentRequest) => string[];

function applyValidators(item: HighlightsButtonComponentRequest, validators: ButtonValidator[]): string[] {
	return validators.flatMap((validator) => validator(item));
}

function requireMainText(item: HighlightsButtonComponentRequest) {
	if (isEmptyString(item.meta.mainText)) {
		return ['highlights.validation.button-text-required'];
	}
	return [];
}

function requireHref(item: HighlightsButtonComponentRequest) {
	if (isEmptyString(item.meta.href) || item.meta.href === 'https://') {
		return ['highlights.validation.button-href-required'];
	}
	return [];
}

function requireContent(item: HighlightsButtonComponentRequest) {
	if (!item.meta.contentId || !item.meta.contentSource) {
		return ['highlights.validation.button-content-required'];
	}
	return [];
}

function requireUserId(item: HighlightsButtonComponentRequest) {
	if (!item.meta.userId) {
		return ['highlights.validation.button-user-required'];
	}
	return [];
}

function requireCategories(item: HighlightsButtonComponentRequest) {
	if (!item.meta.contentSource) {
		return ['highlights.validation.button-categories-required'];
	}
	return [];
}

function requireEvent(item: HighlightsButtonComponentRequest) {
	if (!item.meta.eventId) {
		return ['highlights.validation.button-event-required'];
	}
	return [];
}
